/**
 * ProjectionDeltaCard (MASTER-PRD §6.5.7) — current vs simulated Outcomes
 * side-by-side, with the delta and its EXPLANATION (which replayed events
 * diverged). Transparency is the product, not just the number: every delta
 * carries the model that produced it.
 */

import { Card } from "../card";
import { Badge } from "../badge";
import { cn } from "../utils";

export interface ProjectionOutcomeSummary {
  total: number;
  fulfilled: number;
  fulfillmentRate: number;
}

export interface ProjectionDeltaCardProps {
  scenario: string;
  baseline: ProjectionOutcomeSummary;
  projected: ProjectionOutcomeSummary;
  fulfilledDelta: number;
  explanation: string;
  className?: string;
}

function OutcomeColumn({
  label,
  summary,
  emphasized,
}: {
  label: string;
  summary: ProjectionOutcomeSummary;
  emphasized?: boolean;
}) {
  return (
    <div className="flex-1">
      <p className="type-subtext text-muted-foreground">{label}</p>
      <p className={cn("type-heading font-medium", emphasized && "text-primary")}>
        {summary.fulfilled}
        <span className="type-body font-normal text-muted-foreground">
          {" "}
          / {summary.total}
        </span>
      </p>
      <p className="type-subtext text-muted-foreground">
        {(summary.fulfillmentRate * 100).toFixed(0)}% fulfilled
      </p>
    </div>
  );
}

export function ProjectionDeltaCard({
  scenario,
  baseline,
  projected,
  fulfilledDelta,
  explanation,
  className,
}: ProjectionDeltaCardProps) {
  const sign = fulfilledDelta > 0 ? "+" : "";
  return (
    <Card className={className}>
      <div className="flex flex-col gap-4 px-6 py-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="type-title font-medium">{scenario}</h3>
          <Badge
            variant={
              fulfilledDelta > 0
                ? "success"
                : fulfilledDelta < 0
                  ? "danger"
                  : "neutral"
            }
          >
            {sign}
            {fulfilledDelta} Outcome{Math.abs(fulfilledDelta) === 1 ? "" : "s"}
          </Badge>
        </div>
        <div className="flex gap-6">
          <OutcomeColumn label="Current" summary={baseline} />
          <OutcomeColumn label="Simulated" summary={projected} emphasized />
        </div>
        <p className="border-t border-border/40 pt-3 type-subtext text-muted-foreground">
          {explanation}
        </p>
      </div>
    </Card>
  );
}
